// @ts-nocheck
import PropTypes from "prop-types";
import { useMemo, useState } from "react";
import { CustomIconButton } from "../Custom/CustomIconButton";
import { Box } from "@mui/system";
import { getCssVariableHandler } from "../../utils/styles/getCssVariables";
import { Divider, Menu, MenuItem, Typography } from "@mui/material";
import { FilterAlt } from "@mui/icons-material";
import { IoCheckboxOutline } from "react-icons/io5";
import { IoSquareOutline } from "react-icons/io5";
import { CustomSwitch } from "../Custom/Switch";
import { HiOutlineArrowNarrowUp } from "react-icons/hi";
import { HiOutlineArrowNarrowDown } from "react-icons/hi";

/**
 * The status filter menu (Ongoing / Upcoming / Past / Cancelled / Archived).
 *
 * Shared by the toolbar funnel button (`DashboardFilter`) and by the
 * "Status" column header (`StatusHeaderFilter`) so both surfaces render the
 * same options and mutate the same `searchBy` state.
 */
export function DashboardFilterMenu({
  anchorEl,
  open,
  onClose,
  onChange,
  onClear,
  value,
  filterByOptions,
  hideIncludeArchive,
  anchorOrigin,
  transformOrigin,
  disableAutoFocusItem,
  extraContent,
  extraTitle,
}) {
  const selectedValues = useMemo(() => {
    return value?.map((item) => item?.value);
  }, [value]);

  function optionClickHandler(item) {
    if (selectedValues?.includes(item?.value)) {
      onChange((prev) => prev.filter((p) => p.value !== item.value));
    } else {
      onChange((prev) => [...prev, { ...item, subSort: item?.subSort }]);
    }
  }

  function subSortClickHandler(clickedItem) {
    onChange((prev) => {
      const isAlreadySelected = prev.some(
        (stateItem) => stateItem.value === clickedItem.value,
      );

      if (isAlreadySelected) {
        return prev.map((stateItem) => {
          if (stateItem.value === clickedItem.value) {
            const newSort = stateItem.subSort === "asc" ? "desc" : "asc";
            return { ...stateItem, subSort: newSort };
          }
          return stateItem;
        });
      } else {
        return [...prev, { ...clickedItem, subSort: clickedItem?.subSort }];
      }
    });
  }

  const getSelectedItem = (itemValue) => {
    const foundItem = value?.find((v) => v.value === itemValue);
    if (!foundItem) {
      return filterByOptions?.find((v) => v.value === itemValue);
    } else {
      return foundItem;
    }
  };

  if (!anchorEl) return null;

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(open)}
      onClose={onClose}
      disableAutoFocusItem={disableAutoFocusItem || Boolean(extraContent)}
      anchorOrigin={
        anchorOrigin ?? {
          horizontal: "right",
          vertical: "bottom",
        }
      }
      transformOrigin={
        transformOrigin ?? {
          horizontal: "right",
          vertical: "top",
        }
      }
      sx={{
        "& .MuiPaper-root": {
          borderRadius: "0.5rem !important",
        },
        // Only the menu's own list — `& ul` would also hit lists nested in
        // `extraContent` (e.g. the coordinator list) and kill their scrolling.
        "& .MuiMenu-list": {
          border: `1px solid ${getCssVariableHandler({ variableName: "--primary-pink-hex" })}`,
          borderRadius: "0.5rem",
          overflow: "hidden",
          // The global `.MuiMenu-list` caps the list at 500px; with an extra
          // section the menu is taller than that. Lift the cap so nothing is
          // clipped and there is no outer scrollbar — the extra section scrolls
          // its own list (the Paper still scrolls on very short viewports).
          ...(extraContent ? { maxHeight: "none" } : {}),
        },
        "& .MuiMenu-list > li:hover": {
          backgroundColor: getCssVariableHandler({
            variableName: "--primary-pink-hex",
          }),
        },
      }}
    >
      {filterByOptions?.map((item) => {
        const selectedItem = getSelectedItem(item.value);
        return (
          <MenuItem
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "1rem",
            }}
            key={item?.value}
            onClick={(e) => {
              optionClickHandler(item);
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            {selectedValues?.includes(item?.value) ? (
              <IoCheckboxOutline size={22} />
            ) : (
              <IoSquareOutline size={22} />
            )}
            <Typography>{item?.label}</Typography>
            <CustomIconButton
              sx={{
                padding: "0",
                opacity: selectedValues?.includes(item?.value) ? 1 : 0.6,
              }}
              onClick={(e) => {
                subSortClickHandler(item);
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "flex-start",
                  gap: "0.25rem",
                  border: "1px solid",
                  borderColor: "white",
                  borderRadius: "0.5rem",
                  padding: "0.15rem 0 0.15rem 0.15rem",
                }}
              >
                <Typography sx={{ fontSize: "10px", lineHeight: "1.02" }}>
                  A<br />Z
                </Typography>
                {selectedItem?.subSort == "asc" ? (
                  <HiOutlineArrowNarrowDown size={22} />
                ) : (
                  <HiOutlineArrowNarrowUp size={22} />
                )}
              </Box>
            </CustomIconButton>
          </MenuItem>
        );
      })}
      {!hideIncludeArchive && (
        <>
          <Divider
            sx={{
              borderColor: getCssVariableHandler({
                variableName: "--primary-pink-hex",
              }),
            }}
            orientation="horizontal"
          />
          <Typography
            sx={{
              fontSize: "1rem",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "row",
              px: "0.25rem",
            }}
          >
            <div
              style={{
                scale: "0.8",
              }}
            >
              <CustomSwitch />
            </div>
            Include Archive
          </Typography>
        </>
      )}
      {extraContent ? (
        // Extra filter section (e.g. the dashboard's coordinator filter) that
        // shares this menu so the toolbar funnel shows every active filter.
        <>
          <Divider
            sx={{
              borderColor: getCssVariableHandler({
                variableName: "--primary-pink-hex",
              }),
            }}
            orientation="horizontal"
          />
          {extraTitle ? (
            <Typography
              sx={{
                fontSize: "0.85rem",
                fontWeight: 700,
                px: "1rem",
                pt: "0.5rem",
              }}
            >
              {extraTitle}
            </Typography>
          ) : null}
          {extraContent}
        </>
      ) : null}
      <Typography
        onClick={onClear}
        sx={{
          fontSize: "0.8rem",
          px: "1rem",
          width: "100%",
          textAlign: "right",
          cursor: "pointer",
          userSelect: "none",
          "&:hover": {
            textDecoration: "underline",
          },
          "&:active": {
            color: getCssVariableHandler({
              variableName: "--primary-pink-hex",
            }),
          },
        }}
      >
        clear all
      </Typography>
    </Menu>
  );
}

DashboardFilterMenu.propTypes = {
  anchorEl: PropTypes.any,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onChange: PropTypes.func,
  onClear: PropTypes.func,
  value: PropTypes.array,
  filterByOptions: PropTypes.array,
  hideIncludeArchive: PropTypes.bool,
  anchorOrigin: PropTypes.object,
  transformOrigin: PropTypes.object,
  disableAutoFocusItem: PropTypes.bool,
  extraContent: PropTypes.node,
  extraTitle: PropTypes.node,
};

/**
 * Toolbar funnel button that opens the status filter menu on click.
 * `extraContent`/`extraTitle` append another filter section to the menu and
 * `extraCount` adds its active selections to the badge.
 */
export default function DashboardFilter({
  onChange,
  onClear,
  value,
  filterByOptions,
  iconSize,
  hideIncludeArchive,
  extraContent,
  extraTitle,
  extraCount = 0,
}) {
  const [anchorMenu, setAnchorMenu] = useState({ status: null });

  function handleCloseAnchorMenu() {
    setAnchorMenu({ status: null });
  }

  function btnClickHandler(e) {
    e.stopPropagation();
    setAnchorMenu({ status: e.currentTarget });
  }

  const activeCount = (value?.length ?? 0) + (extraCount || 0);

  return (
    <>
      <CustomIconButton
        aria-label="Dashboard filters"
        sx={{
          position: "relative",
          bgcolor: getCssVariableHandler({
            variableName: "--primary-pink-hex",
          }),
          background:
            "linear-gradient(99deg, #F0F 0%, #8F00FF 100.41%) !important",
          boxShadow: "0px 0px 20px 0px rgba(209, 117, 182, 0.40)",
          borderRadius: "0.5rem",
          border: "1px solid white",
          p: "0.20rem",
          "&:hover": {
            bgcolor: getCssVariableHandler({
              variableName: "--primary-pink-hex",
            }),
          },
        }}
        onClick={btnClickHandler}
      >
        {activeCount > 0 ? (
          <Typography
            sx={{
              position: "absolute",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              top: "-0.35rem",
              right: "-0.35rem",
              borderRadius: "50%",
              p: "0.2rem",
              height: "1.2em",
              border: "1px solid",
              borderColor: getCssVariableHandler({
                variableName: "--white-rgb",
              }),
              aspectRatio: 1,
              bgcolor: getCssVariableHandler({
                variableName: "--primary-pink-hex",
              }),
            }}
          >
            {activeCount}
          </Typography>
        ) : null}
        <FilterAlt size={iconSize ?? 28} />
      </CustomIconButton>
      <DashboardFilterMenu
        anchorEl={anchorMenu?.status}
        open={Boolean(anchorMenu?.status)}
        onClose={handleCloseAnchorMenu}
        onChange={onChange}
        onClear={onClear}
        value={value}
        filterByOptions={filterByOptions}
        hideIncludeArchive={hideIncludeArchive}
        extraContent={extraContent}
        extraTitle={extraTitle}
      />
    </>
  );
}

DashboardFilter.propTypes = {
  onChange: PropTypes.func,
  onClear: PropTypes.func,
  value: PropTypes.array,
  filterByOptions: PropTypes.array,
  iconSize: PropTypes.any,
  hideIncludeArchive: PropTypes.bool,
  extraContent: PropTypes.node,
  extraTitle: PropTypes.node,
  extraCount: PropTypes.number,
};
